import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { EngineerTaskOption, EngineerVisit } from "./engineerVisitTypes";
import {
  createVisit,
  engineerVisitsErrorMessage,
  updateVisit,
} from "./useEngineerVisits";
import { fromDateTimeLocal, toDateTimeLocal } from "./engineerVisitUtils";

export type EngineerVisitStore = {
  locationId: number;
  name: string;
};

type Props = {
  open: boolean;
  /** null = log a new visit */
  visit: EngineerVisit | null;
  stores: EngineerVisitStore[];
  taskOptions: EngineerTaskOption[];
  engineerNames: string[];
  defaultLocationId?: number | null;
  onClose: () => void;
  onSaved: (visit: EngineerVisit) => void;
};

const EngineerVisitModal = ({
  open,
  visit,
  stores,
  taskOptions,
  engineerNames,
  defaultLocationId,
  onClose,
  onSaved,
}: Props) => {
  const [locationId, setLocationId] = useState<number | "">("");
  const [engineerName, setEngineerName] = useState("");
  const [visitedAt, setVisitedAt] = useState("");
  const [tasks, setTasks] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLocationId(visit?.locationId ?? defaultLocationId ?? "");
    setEngineerName(visit?.engineerName ?? "");
    setVisitedAt(toDateTimeLocal(visit?.visitedAt));
    setTasks(visit?.tasks ?? []);
    setNotes(visit?.notes ?? "");
    setBusy(false);
    setError(null);
  }, [open, visit, defaultLocationId]);

  const visitedAtIso = fromDateTimeLocal(visitedAt);
  const canSubmit =
    !busy &&
    locationId !== "" &&
    engineerName.trim() !== "" &&
    visitedAtIso != null &&
    (tasks.length > 0 || notes.trim() !== "");

  const toggleTask = (key: string) => {
    setTasks((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const input = {
      locationId,
      engineerName: engineerName.trim(),
      visitedAt: visitedAtIso,
      tasks: taskOptions.map((o) => o.key).filter((k) => tasks.includes(k)),
      notes: notes.trim() || null,
    };
    try {
      const saved = visit ? await updateVisit(visit.id, input) : await createVisit(input);
      onSaved(saved);
    } catch (err: any) {
      setError(engineerVisitsErrorMessage(err, "Failed to save the visit."));
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <span>{visit ? "Edit engineer visit" : "Log engineer visit"}</span>
        <IconButton aria-label="close" onClick={handleClose} size="small" disabled={busy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <TextField
          select
          label="Store"
          required
          fullWidth
          value={locationId}
          onChange={(e) => setLocationId(Number(e.target.value))}
          disabled={busy}
          sx={{ mb: 2 }}
        >
          {stores.map((s) => (
            <MenuItem key={s.locationId} value={s.locationId}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>

        <Autocomplete
          freeSolo
          options={engineerNames}
          inputValue={engineerName}
          onInputChange={(_e, value) => setEngineerName(value)}
          disabled={busy}
          renderInput={(params) => (
            <TextField {...params} label="Engineer" required fullWidth sx={{ mb: 2 }} />
          )}
        />

        <TextField
          type="datetime-local"
          label="Visited at"
          required
          fullWidth
          value={visitedAt}
          onChange={(e) => setVisitedAt(e.target.value)}
          disabled={busy}
          error={visitedAt !== "" && visitedAtIso == null}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { max: toDateTimeLocal(null) },
          }}
          sx={{ mb: 2 }}
        />

        <FormControl component="fieldset" sx={{ mb: 2 }} disabled={busy}>
          <FormLabel component="legend">What they did</FormLabel>
          <FormGroup>
            {taskOptions.map((o) => (
              <FormControlLabel
                key={o.key}
                control={
                  <Checkbox checked={tasks.includes(o.key)} onChange={() => toggleTask(o.key)} />
                }
                label={o.label}
              />
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          label="Notes"
          multiline
          minRows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
          placeholder="Anything else they did or found"
          helperText="Tick at least one task or add notes"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {busy ? "Saving…" : visit ? "Save changes" : "Log visit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngineerVisitModal;
